import React, { createContext, useContext, useReducer, useEffect } from 'react';
    import { fmvData as initialFmvData } from '../data/fmvData';

    const DonationContext = createContext();

    const initialState = {
      donor: { name: '', address: '', city: '', zip: '', phone: '', email: '' },
      donations: [],
      photos: [],
      currentDonation: null,
      fmvItems: initialFmvData,
      editingItemId: null,
      taxBracket: 22
    };

    function donationReducer(state, action) {
      switch (action.type) {
        case 'SET_DONOR':
          return { ...state, donor: action.payload };
        case 'SET_TAX_BRACKET':
          return { ...state, taxBracket: action.payload };
        case 'ADD_PHOTO':
          const newPhoto = {
            id: state.photos.length + 1,
            file: action.payload.file,
            url: action.payload.url,
            timestamp: new Date().toISOString()
          };
          return { ...state, photos: [...state.photos, newPhoto] };
        case 'START_DONATION':
          const newDonation = {
            id: Date.now(),
            date: action.payload.date,
            location: action.payload.location,
            charity: action.payload.charity,
            items: [],
            total: 0
          };
          return { ...state, currentDonation: newDonation, donations: [...state.donations, newDonation] };
        case 'UPDATE_DONATION_DATE':
          if (!state.currentDonation) return state;
          const updatedDonationsDate = state.donations.map(d => 
            d.id === state.currentDonation.id ? { ...d, date: action.payload } : d
          );
          return {
            ...state,
            donations: updatedDonationsDate,
            currentDonation: { ...state.currentDonation, date: action.payload }
          };
        case 'UPDATE_FMV':
          return {
            ...state,
            fmvItems: state.fmvItems.map(item =>
              item.name === action.payload.name && item.category === action.payload.category
                ? { ...item, ...action.payload.updates }
                : item
            )
          };
        case 'ADD_ITEM_TYPE':
          return { ...state, fmvItems: [...state.fmvItems, action.payload] };
        case 'SET_EDITING_ITEM':
          return { ...state, editingItemId: action.payload };
        case 'ADD_ITEM':
          const updatedDonationsAdd = state.donations.map(donation => {
            if (donation.id === state.currentDonation.id) {
              const newItem = { id: Date.now() + Math.random(), ...action.payload };
              const updatedItems = [...donation.items, newItem];
              const newTotal = updatedItems.reduce((sum, item) => sum + item.subtotal, 0);
              return { ...donation, items: updatedItems, total: newTotal };
            }
            return donation;
          });
          const updatedCurrentDonationAdd = updatedDonationsAdd.find(d => d.id === state.currentDonation.id);
          return { ...state, donations: updatedDonationsAdd, currentDonation: updatedCurrentDonationAdd };
        case 'UPDATE_ITEM':
          const updatedDonationsEdit = state.donations.map(donation => {
            if (donation.id === state.currentDonation.id) {
              const updatedItems = donation.items.map(item =>
                item.id === action.payload.id ? { ...item, ...action.payload.updates } : item
              );
              const newTotal = updatedItems.reduce((sum, item) => sum + item.subtotal, 0);
              return { ...donation, items: updatedItems, total: newTotal };
            }
            return donation;
          });
          const updatedCurrentDonationEdit = updatedDonationsEdit.find(d => d.id === state.currentDonation.id);
          return { ...state, donations: updatedDonationsEdit, currentDonation: updatedCurrentDonationEdit, editingItemId: null };
        case 'REMOVE_ITEM':
          const donationsAfterRemoval = state.donations.map(donation => {
            if (donation.id === state.currentDonation.id) {
              const filteredItems = donation.items.filter(item => item.id !== action.payload);
              const newTotal = filteredItems.reduce((sum, item) => sum + item.subtotal, 0);
              return { ...donation, items: filteredItems, total: newTotal };
            }
            return donation;
          });
          const currentAfterRemoval = donationsAfterRemoval.find(d => d.id === state.currentDonation.id);
          return { ...state, donations: donationsAfterRemoval, currentDonation: currentAfterRemoval, editingItemId: state.editingItemId === action.payload ? null : state.editingItemId };
        case 'LOAD_STATE':
          const savedFmv = action.payload.fmvItems || [];
          const mergedFmv = [...initialFmvData];
          savedFmv.forEach(savedItem => {
            const index = mergedFmv.findIndex(i => i.name === savedItem.name && i.category === savedItem.category);
            if (index !== -1) { mergedFmv[index] = savedItem; }
            else { mergedFmv.push(savedItem); }
          });
          return { ...initialState, ...action.payload, fmvItems: mergedFmv, editingItemId: null, taxBracket: action.payload.taxBracket ?? 22 };
        default:
          return state;
      }
    }

    export function DonationProvider({ children }) {
      const [state, dispatch] = useReducer(donationReducer, initialState);
      useEffect(() => {
        const savedState = localStorage.getItem('donationApp');
        if (savedState) {
          try {
            const parsedState = JSON.parse(savedState);
            dispatch({ type: 'LOAD_STATE', payload: parsedState });
          } catch (error) { console.error('Error loading saved state:', error); }
        }
      }, []);
      useEffect(() => { localStorage.setItem('donationApp', JSON.stringify(state)); }, [state]);
      return (
        <DonationContext.Provider value={{ state, dispatch }}>
          {children}
        </DonationContext.Provider>
      );
    }

    export function useDonation() {
      const context = useContext(DonationContext);
      if (!context) throw new Error('useDonation must be used within a DonationProvider');
      return context;
    }